import { createSlotRecipeContext, type HTMLChakraProps } from '@chakra-ui/react'
import { useLayoutEffect, useRef } from 'react'

import { getEnvironmentBannerMeta, type EnvironmentBannerMeta } from '@/config/environment'
import {
  ENVIRONMENT_BANNER_HEIGHT_VAR,
  environmentBannerRecipe,
} from '@/theme/recipes/environment-banner.recipe'

const { withProvider, withContext } = createSlotRecipeContext({ recipe: environmentBannerRecipe })

const Root = withProvider<HTMLElement, HTMLChakraProps<'section'>>('section', 'root')
const Label = withContext<HTMLElement, HTMLChakraProps<'strong'>>('strong', 'label')
const Description = withContext<HTMLParagraphElement, HTMLChakraProps<'p'>>('p', 'description')

/**
 * Permanent notice of the non-productive environment the portal runs in. Mounted unconditionally
 * at the app root; renders nothing where the environment has no banner (production).
 */
export function EnvironmentBanner() {
  const meta = getEnvironmentBannerMeta()
  return meta ? <Banner meta={meta} /> : null
}

function Banner({ meta }: { meta: EnvironmentBannerMeta }) {
  const ref = useRef<HTMLElement>(null)

  // Publishes the banner's height, which changes as the text wraps, so the sticky headers below
  // it can offset themselves instead of sliding underneath.
  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return
    const root = document.documentElement
    const publish = () =>
      root.style.setProperty(
        ENVIRONMENT_BANNER_HEIGHT_VAR,
        `${el.getBoundingClientRect().height}px`
      )

    publish()
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(publish)
    observer?.observe(el)
    return () => {
      observer?.disconnect()
      root.style.removeProperty(ENVIRONMENT_BANNER_HEIGHT_VAR)
    }
  }, [])

  return (
    <Root
      ref={ref}
      role="region"
      aria-label="Hinweis zur Umgebung"
      colorPalette={meta.colorPalette}
    >
      <Label>{meta.label}</Label>
      <Description>{meta.description}</Description>
    </Root>
  )
}
